import React, { useEffect, useRef, useState } from "react";
import { __ } from "services/Translator";
import { getBonusIconMeta } from "utils/getBonusIcon";

function plainCampaignText(value) {
  if (!value) return null;
  const text = String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return text || null;
}

function getSummaryText({ isCampaignsLoading, selectedBonus, isNoBonusSelected }) {
  if (isCampaignsLoading) return __("Loading offers...");
  if (selectedBonus) return selectedBonus.title;
  if (isNoBonusSelected) return __("No bonus");
  return __("Tap to choose an offer");
}

function Option({ active, onSelect, title, subtitle, imageSrc, imageFallback }) {
  return (
    <button
      type="button"
      className={`gcc-bonus-option ${active ? "is-selected" : ""}`}
      onClick={onSelect}
      aria-pressed={active}
    >
      <span className="gcc-bonus-option__radio" aria-hidden="true" />
      <span className="gcc-bonus-option__text">
        <span className="gcc-bonus-option__title">{title}</span>
        {subtitle ? <span className="gcc-bonus-option__sub">{subtitle}</span> : null}
      </span>
      {imageSrc ? (
        <img
          src={imageSrc}
          alt=""
          className={
            "gcc-bonus-option__thumb " + (imageFallback ? "gcc-bonus-option__thumb--icon" : "")
          }
        />
      ) : null}
    </button>
  );
}

export default function SignupBonusDropdown({
  campaigns,
  isCampaignsLoading,
  bonusOffer,
  onSelectBonus,
  hasError,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef(null);
  const selectedBonus = campaigns.find((item) => item.id === bonusOffer) || null;
  const isNoBonusSelected = bonusOffer === "no_bonus";
  const summary = getSummaryText({ isCampaignsLoading, selectedBonus, isNoBonusSelected });

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const handleSelect = (value) => {
    onSelectBonus(value);
    setIsOpen(false);
  };

  return (
    <div
      ref={rootRef}
      className={
        "gcc-bonus-dropdown" + (hasError ? " gcc-bonus-dropdown--error" : "")
      }
    >
      <button
        type="button"
        className={"gcc-bonus-dropdown__trigger" + (isOpen ? " is-open" : "")}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="gcc-bonus-dropdown__summary">{summary}</span>
        <span className="gcc-bonus-dropdown__arrow" aria-hidden="true">
          <svg width="10" height="6" viewBox="0 0 10 6" fill="currentColor">
            <path d="M0 0h10L5 6 0 0z" />
          </svg>
        </span>
      </button>

      {isOpen ? (
        <div className="gcc-bonus-dropdown__panel" role="listbox">
          <div className="gcc-bonus-simple__options">
            {isCampaignsLoading &&
              [0, 1].map((i) => (
                <div key={i} className="gcc-bonus-option gcc-bonus-option--loading" aria-hidden="true">
                  <span className="gcc-bonus-option__radio" />
                  <span className="gcc-bonus-option__text">
                    <span className="gcc-bonus-option__bar" />
                    <span className="gcc-bonus-option__bar gcc-bonus-option__bar--short" />
                  </span>
                </div>
              ))}

            {!isCampaignsLoading &&
              campaigns.map((item) => {
                const icon = getBonusIconMeta(item);
                return (
                  <Option
                    key={item.id}
                    active={bonusOffer === item.id}
                    onSelect={() => handleSelect(item.id)}
                    title={item.title}
                    subtitle={plainCampaignText(item.campaign_information)}
                    imageSrc={icon.src}
                    imageFallback={icon.isFallback}
                  />
                );
              })}

            {!isCampaignsLoading && (
              <Option
                active={isNoBonusSelected}
                onSelect={() => handleSelect("no_bonus")}
                title={__("No bonus")}
                subtitle={__("Continue without an offer")}
              />
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}
